/**
 * Login.tsx — email+password sign-in screen calling Better Auth via
 * AuthContext#login, plus an optional "Sign in with Google" button. No
 * self-registration link either way — accounts only come from a manager's
 * invite (see People). A Google sign-in with no matching invite is
 * rejected by the Worker and lands back here with `?error=`, which this
 * page reads and turns into the same red error banner email/password
 * failures use — see authApi.signInWithGoogle in lib/api.ts.
 */
import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ApiError, authApi, errorText } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { AuthShell } from "@/components/auth/AuthShell";
import { PasswordInput } from "@/components/auth/PasswordInput";

/** Better Auth's own OAuth error codes (see
 * node_modules/better-auth/dist/oauth2/errors.mjs), reworded in plain
 * language for the people using this CMS. Anything not in this map still
 * shows — just as the raw code — rather than silently swallowing it. */
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  signup_disabled:
    "That Google account isn't invited to this CMS yet. Ask a manager to invite your email first, then try Google sign-in again.",
  email_not_found: "Google didn't share an email address with us, so we couldn't sign you in.",
};

export function Login() {
  const { user, login, finishSignIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(() => {
    const oauthError = searchParams.get("error");
    if (!oauthError) return null;
    return OAUTH_ERROR_MESSAGES[oauthError] ?? `Google sign-in failed (${oauthError}).`;
  });
  const [notice] = useState<string | null>(() => (searchParams.get("reset") ? "Your password is saved. Sign in with it." : null));
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Second step for accounts that use two-step sign-in.
  const [step, setStep] = useState<"password" | "code">("password");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { needsCode } = await login(email, password);
      if (needsCode) {
        setStep("code");
        setCode("");
      }
    } catch (err) {
      setError(errorText(err, "Sign in failed."));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode(value: string) {
    setError(null);
    setSubmitting(true);
    try {
      if (useBackup) await authApi.verifyBackupCode(value);
      else await authApi.verifyCode(value);
      await finishSignIn();
    } catch (err) {
      setError(errorText(err, "That code did not work."));
      setCode("");
      // Too many tries, or the sign-in timed out: the password step starts over.
      if (err instanceof ApiError && (err.code === "INVALID_TWO_FACTOR_COOKIE" || err.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE")) backToPassword(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function backToPassword(message: string | null = null) {
    setStep("password");
    setPassword("");
    setCode("");
    setUseBackup(false);
    setError(message);
  }

  async function handleGoogleSignIn() {
    setError(null);
    setGoogleLoading(true);
    try {
      // Navigates away to Google on success, so `setGoogleLoading(false)`
      // only ever runs on the failure path.
      await authApi.signInWithGoogle();
    } catch (err) {
      setError(errorText(err, "Couldn't start Google sign-in."));
      setGoogleLoading(false);
    }
  }

  if (step === "code") {
    return (
      <AuthShell title="Enter your code" subtitle={useBackup ? "Type one of your backup codes. Each one works once." : "Open your authenticator app and type the 6-digit code for DemandHub CMS."}>
        {error && <Alert tone="danger" className="mt-6">{error}</Alert>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) void submitCode(code);
          }}
          className="mt-8 space-y-5"
        >
          <div>
            <Label htmlFor="code">{useBackup ? "Backup code" : "6-digit code"}</Label>
            <Input
              id="code"
              autoFocus
              required
              value={code}
              inputMode={useBackup ? "text" : "numeric"}
              autoComplete="one-time-code"
              spellCheck={false}
              maxLength={useBackup ? 32 : 7}
              className={useBackup ? "" : "h-12 text-center font-mono text-xl tracking-[0.3em]"}
              onChange={(e) => {
                const v = useBackup ? e.target.value : e.target.value.replace(/[^\d ]/g, "");
                setCode(v);
                if (!useBackup && v.replace(/\s/g, "").length === 6 && !submitting) void submitCode(v);
              }}
            />
          </div>
          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            {submitting ? "Checking…" : "Continue"}
          </Button>
        </form>
        <div className="mt-6 flex items-center justify-between text-sm">
          <button type="button" className="text-accent-700 hover:underline" onClick={() => { setUseBackup((b) => !b); setCode(""); setError(null); }}>
            {useBackup ? "Use my authenticator app" : "Use a backup code instead"}
          </button>
          <button type="button" className="text-neutral-500 hover:text-neutral-800" onClick={() => backToPassword()}>
            Back
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to the DemandHub CMS.">
      {notice && <Alert tone="success" className="mt-6">{notice}</Alert>}
      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}
      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-neutral-700">Password</label>
            <Link to="/forgot-password" state={{ email }} className="text-xs text-accent-700 hover:underline">Forgot password?</Link>
          </div>
          <PasswordInput id="password" label="" autoComplete="current-password" value={password} onChange={setPassword} bare />
        </div>
        <Button type="submit" className="w-full" size="lg" loading={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-neutral-200" />
        <span className="text-sm text-neutral-500">or</span>
        <div className="h-px flex-1 bg-neutral-200" />
      </div>

      <Button type="button" variant="secondary" className="w-full gap-2.5" size="lg" loading={googleLoading} onClick={handleGoogleSignIn}>
        {!googleLoading && <GoogleIcon className="size-4" />}
        {googleLoading ? "Redirecting to Google…" : "Sign in with Google"}
      </Button>
    </AuthShell>
  );
}

/** Google's standard multi-color "G" mark, per Google's own sign-in button
 * branding guidelines (https://developers.google.com/identity/branding-guidelines) —
 * this exact icon is what every "Sign in with Google" button uses. */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.82Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.11A12 12 0 0 0 12 24Z"
      />
      <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.61H1.27a12 12 0 0 0 0 10.78l4-3.11Z" />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.27 6.61l4 3.11C6.22 6.86 8.87 4.75 12 4.75Z"
      />
    </svg>
  );
}
